import React, { useMemo } from 'react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from 'recharts';

/**
 * ViralScoreRadar — signature visualization for AI video analysis.
 * Derives a 5-dimension radar (Hook / Topic / Audience / Industry / Clarity)
 * from the analysis object; falls back to heuristic scoring if no scores provided.
 *
 * Input: `analysis` object shape:
 *   { hook_text, hook_analysis, topic, industry, target_audience }
 *   optional numeric scores: { hook_score, topic_score, audience_score, industry_score, clarity_score } (0-10)
 */
const ViralScoreRadar = ({ analysis, size = 260 }) => {
  const data = useMemo(() => {
    if (!analysis) return [];

    // Heuristic scoring — text length + presence → score 0-10
    const score = (text, max = 80) => {
      if (!text) return 2;
      const len = String(text).trim().length;
      return Math.min(10, Math.max(2, Math.round((len / max) * 10)));
    };

    return [
      {
        dim: 'Hook 抓手',
        score: analysis.hook_score ?? score((analysis.hook_analysis || '') + (analysis.hook_text || ''), 120),
        full: 10,
      },
      {
        dim: '主題清晰',
        score: analysis.topic_score ?? score(analysis.topic, 40),
        full: 10,
      },
      {
        dim: '目標受眾',
        score: analysis.audience_score ?? score(analysis.target_audience, 40),
        full: 10,
      },
      {
        dim: '行業契合',
        score: analysis.industry_score ?? score(analysis.industry, 20),
        full: 10,
      },
      {
        dim: '內容完整',
        score:
          analysis.clarity_score ??
          score(
            [analysis.hook_text, analysis.topic, analysis.target_audience, analysis.industry].filter(Boolean).join(' '),
            200
          ),
        full: 10,
      },
    ];
  }, [analysis]);

  const total = data.reduce((a, b) => a + b.score, 0);
  const overall = data.length > 0 ? Math.round((total / (data.length * 10)) * 100) : 0;

  const scoreColor =
    overall >= 80 ? '#FF5436' : overall >= 60 ? '#FF6B50' : overall >= 40 ? '#FBBF24' : '#F87171';
  const scoreLabel = overall >= 80 ? '強爆款' : overall >= 60 ? '潛力款' : overall >= 40 ? '一般' : '需優化';

  if (!analysis) return null;

  return (
    <div
      className="relative rounded-xl border border-obsidian-600 bg-obsidian-800 p-5"
      data-testid="viral-score-radar"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="label-micro text-lime-500 mb-1">VIRAL · SCORE</div>
          <div className="font-display font-semibold text-white text-lg tracking-tight">爆款指數</div>
        </div>
        <div className="text-right">
          <div
            className="font-display font-semibold tabular-nums leading-none text-4xl"
            style={{ color: scoreColor, textShadow: `0 0 16px ${scoreColor}55` }}
          >
            {overall}
          </div>
          <div className="label-micro mt-1" style={{ color: scoreColor }}>
            {scoreLabel}
          </div>
        </div>
      </div>

      {/* Radar chart */}
      <div className="w-full" style={{ height: size }}>
        <ResponsiveContainer>
          <RadarChart data={data} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
            <PolarGrid stroke="#2E3036" strokeDasharray="2 3" />
            <PolarAngleAxis
              dataKey="dim"
              tick={{ fill: '#A0A5AD', fontSize: 11, fontWeight: 500 }}
            />
            <PolarRadiusAxis
              angle={90}
              domain={[0, 10]}
              tick={{ fill: '#52555C', fontSize: 9 }}
              tickCount={4}
              stroke="#2E3036"
              axisLine={false}
            />
            <Radar
              dataKey="score"
              stroke="#FF5436"
              strokeWidth={2}
              fill="#FF5436"
              fillOpacity={0.22}
              isAnimationActive
              animationDuration={900}
            />
            <Tooltip
              contentStyle={{
                background: '#0E1014',
                border: '1px solid #222428',
                borderRadius: 8,
                padding: '8px 12px',
                color: '#FFFFFF',
                fontSize: 12,
              }}
              cursor={{ stroke: 'rgba(255,84,54,0.3)' }}
              formatter={(v) => [`${v} / 10`, '分數']}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Dimension detail grid */}
      <div className="grid grid-cols-5 gap-2 mt-3 pt-3 border-t border-obsidian-600">
        {data.map((d) => (
          <div key={d.dim} className="text-center">
            <div className="label-micro text-obsidian-400 truncate mb-0.5">{d.dim}</div>
            <div
              className="font-mono tabular-nums text-sm font-semibold"
              style={{ color: d.score >= 7 ? '#FF5436' : d.score >= 5 ? '#FF6B50' : '#A0A5AD' }}
            >
              {d.score}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ViralScoreRadar;
